import { resolverValor } from './parser.js'
import { funcionesPersonalizadas } from '../funciones/index.js'

function findTopLevelColon(str) {
  let inQuotes = false
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (escape) {
      escape = false
      continue
    }
    if (c === '\\' && inQuotes) {
      escape = true
      continue
    }
    if (c === '"') {
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && c === '(') parenDepth++
    if (!inQuotes && c === ')') parenDepth = Math.max(0, parenDepth - 1)
    if (!inQuotes && parenDepth === 0 && c === ':') return i
  }
  return -1
}

function splitArgs(str) {
  const args = []
  let current = ''
  let inQuotes = false
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (escape) {
      current += c
      escape = false
      continue
    }
    if (c === '\\' && inQuotes) {
      current += c
      escape = true
      continue
    }
    if (c === '"') {
      current += c
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && c === '(') {
      parenDepth++
      current += c
      continue
    }
    if (!inQuotes && c === ')') {
      parenDepth = Math.max(0, parenDepth - 1)
      current += c
      continue
    }
    if (!inQuotes && parenDepth === 0 && c === ',') {
      args.push(current.trim())
      current = ''
      continue
    }
    current += c
  }

  if (current.trim().length > 0) args.push(current.trim())
  return args
}

function stripWrapper(str) {
  let s = str.trim()
  if (s.startsWith('${') && s.endsWith('}')) {
    s = s.slice(2, -1).trim()
  }
  return s
}

export function parseExpression(str, errors = []) {
  let s = stripWrapper(str)

  if (s.startsWith('(') && s.endsWith(')')) {
    const inner = s.slice(1, -1).trim()
    return parseExpression(inner, errors)
  }

  if (s.startsWith('v:') || s.startsWith('i:')) {
    return { type: 'var', value: s }
  }

  const colonPos = findTopLevelColon(s)
  if (colonPos !== -1) {
    const name = s.slice(0, colonPos).trim()
    const argsRaw = s.slice(colonPos + 1).trim()
    const args = splitArgs(argsRaw).map((a) => parseArg(a, errors))
    return { type: 'func', name, args }
  }

  if (s.startsWith('"') && s.endsWith('"')) {
    const literal = s.slice(1, -1).replace(/\\"/g, '"')
    return { type: 'lit', value: literal }
  }

  if (!s) {
    errors.push({ code: 'empty', message: 'Expresión vacía' })
    return { type: 'lit', value: '' }
  }

  return { type: 'alias', value: s }
}

function parseArg(raw, errors) {
  const s = raw.trim()

  if (s.startsWith('(') && s.endsWith(')')) {
    const inner = s.slice(1, -1).trim()
    return { type: 'func', ...parseExpression(inner, errors) }
  }

  if (s.startsWith('v:') || s.startsWith('i:')) {
    return { type: 'var', value: s }
  }

  if (s.startsWith('"') && s.endsWith('"')) {
    const literal = s.slice(1, -1).replace(/\\"/g, '"')
    return { type: 'lit', value: literal }
  }

  const colonPos = findTopLevelColon(s)
  if (colonPos !== -1) {
    errors.push({
      code: 'nested_func_needs_parens',
      message: 'Función directa como argumento debe ir entre paréntesis',
      detail: s,
    })
    return { type: 'lit', value: s }
  }

  return parseExpression(s, errors)
}

export function normalizeNode(
  node,
  aliasToVariable,
  aliasToExpresion,
  pathToVariable,
  errors = []
) {
  if (node.type === 'alias') {
    if (aliasToVariable[node.value]) {
      const vExp = aliasToVariable[node.value].expresion || ''
      return normalizeNode(
        parseExpression(vExp, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    if (aliasToExpresion[node.value]) {
      const eExp = aliasToExpresion[node.value].expresion || ''
      return normalizeNode(
        parseExpression(eExp, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    if (pathToVariable && pathToVariable[node.value]) {
      const vExp = pathToVariable[node.value].expresion || `\${v:${node.value}}`
      return normalizeNode(
        parseExpression(vExp, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    // Si parece ruta (contiene . o []), tratar como variable directa
    if (/[.\[]/.test(node.value)) {
      const vExp = `\${v:${node.value}}`
      return normalizeNode(
        parseExpression(vExp, errors),
        aliasToVariable,
        aliasToExpresion,
        pathToVariable,
        errors
      )
    }
    errors.push({
      code: 'alias_not_found',
      message: 'Alias no encontrado',
      detail: node.value,
    })
    return { type: 'lit', value: node.value }
  }

  if (node.type === 'func') {
    return {
      type: 'func',
      name: node.name,
      args: (node.args || []).map((a) =>
        normalizeNode(a, aliasToVariable, aliasToExpresion, pathToVariable, errors)
      ),
    }
  }

  return node
}

export function stringifyNode(node) {
  if (node.type === 'lit') {
    const safe = String(node.value).replace(/"/g, '\\"')
    return `"${safe}"`
  }
  if (node.type === 'var') return node.value
  if (node.type === 'func') {
    const args = (node.args || []).map((a) => {
      const s = stringifyNode(a)
      if (a.type === 'func') return `(${s})`
      return s
    })
    return `${node.name}:${args.join(',')}`
  }
  if (node.type === 'alias') return node.value
  return ''
}

export function evalNode(node, datos, errors = []) {
  if (node.type === 'lit') return node.value
  if (node.type === 'var') {
    const path = node.value.replace(/^(v:|i:)/, '')
    return resolverValor(datos, path)
  }
  if (node.type === 'func') {
    const fn = funcionesPersonalizadas[node.name]
    if (!fn) {
      errors.push({
        code: 'func_not_found',
        message: 'Función no encontrada',
        detail: node.name,
      })
      return ''
    }
    const vals = (node.args || []).map((a) => evalNode(a, datos, errors))
    try {
      return fn(...vals)
    } catch (e) {
      errors.push({
        code: 'func_error',
        message: `Error ejecutando ${node.name}`,
        detail: e?.message || String(e),
      })
      return ''
    }
  }
  return ''
}
